import { CommentsFactory } from '@getaccept/lib-shared/src/contextual-commenting/helpers/comments.factory';
import { CommentsHelper } from '@getaccept/lib-shared/src/contextual-commenting/helpers/comments.helper';
import { useContextualCommentingStore } from '@getaccept/lib-shared/src/contextual-commenting/store/contextual-commenting.store';
import type { Comment } from '@getaccept/lib-shared/src/contextual-commenting/types/comment';
import type {
  CommentReadPayload,
  ContextualCommentBase,
  ReplyReadPayload,
} from '@getaccept/lib-shared/src/pusher/contextual-commenting/contextual-commenting-payloads';
import { CommentsService } from '../../../api/contextual-commenting/services/comments.service';
import { useRootStore } from '../../../store/root.store';

export const useContextualCommentingPusherData = () => {
  const contextualCommentingStore = useContextualCommentingStore();
  const rootStore = useRootStore();

  const onCommentCreated = async (payload: ContextualCommentBase) => {
    const {
      contextualComment: { commentId },
    } = payload;

    if (CommentsHelper.commentExists(contextualCommentingStore.comments, commentId)) {
      return;
    }
    try {
      const comment = await CommentsService.loadOne(commentId);
      const isSameUser =
        rootStore.recipientId === comment.createdBy?.recipientId ||
        rootStore.userId === comment.createdBy?.userId;
      if (isSameUser) {
        return;
      }
      contextualCommentingStore.addComment(comment);
    } catch (e) {
      console.error('error', e);
    }
  };

  const onCommentUpdated = async (payload: ContextualCommentBase) => {
    const {
      contextualComment: { commentId },
    } = payload;

    try {
      const comment: Comment = await CommentsService.loadOne(commentId);
      if (!CommentsHelper.commentExists(contextualCommentingStore.comments, comment.id)) {
        contextualCommentingStore.addComment(comment);
        return;
      }
      if (
        CommentsHelper.commentAlreadyUpdated(contextualCommentingStore.comments, commentId, comment)
      ) {
        return;
      }
      contextualCommentingStore.replaceComment(comment);
    } catch (e) {
      console.error('error', e);
    }
  };

  const onCommentRead = (payload: CommentReadPayload) => {
    const {
      contextualComment: { commentId, readBy },
    } = payload;

    if (!readBy.userId && !readBy.recipientId) {
      return;
    }

    const comment = CommentsHelper.findComment(contextualCommentingStore.comments, commentId);
    if (!comment) {
      return;
    }

    if (CommentsHelper.isCommentRead(comment as any, readBy)) {
      return;
    }

    const readEvent = CommentsFactory.readEvent({
      participant: CommentsFactory.commentParticipant(readBy),
    });
    contextualCommentingStore.addReadEvent(commentId, readEvent);
  };

  const onReplyRead = (payload: ReplyReadPayload) => {
    const {
      contextualComment: { commentId, replyId, readBy },
    } = payload;

    if (!readBy.userId && !readBy.recipientId) {
      return;
    }

    const reply = CommentsHelper.findReply(contextualCommentingStore.comments, commentId, replyId);
    if (!reply) {
      return;
    }

    if (CommentsHelper.isRead(reply.reads, readBy)) {
      return;
    }

    const readEvent = CommentsFactory.readEvent({
      participant: CommentsFactory.commentParticipant(readBy),
    });

    contextualCommentingStore.addReplyReadEvent(commentId, replyId, readEvent);
  };

  const onCommentDeleted = (payload: ContextualCommentBase) => {
    const {
      contextualComment: { commentId },
    } = payload;
    contextualCommentingStore.removeComment(commentId);
  };

  return {
    onCommentUpdated,
    onCommentRead,
    onReplyRead,
    onCommentCreated,
    onCommentDeleted,
  };
};
